<template>
  <div class="initial-medication">
    <div class="accordion" data-testid="accordions-container">
      <div class="accordion-item" data-testid="medication-fluid-accordion-container">
        <md-button
          class="md-raised accordion-trigger"
          :class="{ open: isActivePanel('MEDICATION_FLUID_SELECT') }"
          :disabled="!caseConf.iv.enabled"
          aria-label="Medication/Fluid"
          data-testid="medication-fluid-accordion-button"
          @click="togglePanel('MEDICATION_FLUID_SELECT')"
        >
          <md-icon class="trigger-icon">arrow_right</md-icon>
          <label>Medication/Fluid</label>
        </md-button>
      </div>
      <transition name="fold">
        <div
          v-if="isActivePanel('MEDICATION_FLUID_SELECT')"
          class="accordion-content"
          data-testid="medication-fluid-content-container"
        >
          <div class="med-fluid-container" data-testid="med-fluid-select-container" style="flex: 0.9">
            <md-field>
              <medication-fluid-selector
                :selected="initialMedication.medOrFluidId"
                :case-conf="caseConf"
                :disabled="readonly"
                :medications="medications"
                :dosage-form-filter="
                  dosageForm => dosageForm.enabledRoutes.find(r => r.key === 'IV_IO_INFUSION')
                "
                @select="selectMedicationOrFluid"
              />
              <help-tooltip v-if="medEnabledError" type="error" aria-label="Medication Not Enabled Error" data-testid="medication-not-enabled-error">
                {{ medEnabledError.message }}
              </help-tooltip>
              <help-tooltip v-if="routeEnabledError" type="error" aria-label="Route Not Enabled Error" data-testid="route-not-enabled-error">
                {{ routeEnabledError.message }}
              </help-tooltip>
              <help-tooltip v-if="fluidEnabledError" type="error" aria-label="Fluid Not Enabled Error" data-testid="fluid-not-enabled-error">
                {{ fluidEnabledError.message }}
              </help-tooltip>
            </md-field>
          </div>

          <div class="volume-container" data-testid="volume-select-container" style="flex: 0.9">
            <md-field>
              <infusion-bag-volume-select
                :multiple="false"
                :value="initialMedication.volume || null"
                placeholder="Bag Volume"
                :disabled="readonly"
                :available="availableBagVolumes"
                @input="value => updateField('volume', value)"
              />
              <help-tooltip v-if="volumeEnabledError" type="error" aria-label="Volume Not Enabled Error" data-testid="volume-not-enabled-error">
                {{ volumeEnabledError.message }}
              </help-tooltip>
            </md-field>
          </div>
        </div>
      </transition>

      <div class="accordion-item" data-testid="connection-accordion-container">
        <md-button
          class="md-raised accordion-trigger"
          :class="{ open: isActivePanel('CONNECTION') }"
          :disabled="!caseConf.iv.enabled"
          aria-label="Connection"
          data-testid="connection-accordion-button"
          @click="togglePanel('CONNECTION')"
        >
          <md-icon class="trigger-icon">arrow_right</md-icon>
          <label>Connection</label>
        </md-button>
      </div>
      <transition name="fold">
        <div
          v-if="isActivePanel('CONNECTION')"
          class="accordion-content connection-content"
        >
          <initial-connection
            :case-conf="caseConf"
            :readonly="readonly"
            :medications="medications"
            :initial-medication="initialMedication"
            :connection="initialMedication.connection || {}"
            @update="value => updateField('connection', value)"
          />
        </div>
      </transition>

      <div class="accordion-item" data-testid="control-accordion-container">
        <md-button
          class="md-raised accordion-trigger"
          :class="{ open: isActivePanel('CONTROL') }"
          :disabled="!caseConf.iv.enabled"
          aria-label="Control"
          data-testid="control-accordion-button"
          @click="togglePanel('CONTROL')"
        >
          <md-icon class="trigger-icon">arrow_right</md-icon>
          <label>Control</label>
        </md-button>
      </div>
      <transition name="fold">
        <div
          v-if="isActivePanel('CONTROL')"
          class="accordion-content control-content"
        >
          <initial-control
            :case-conf="caseConf"
            :initial-medication="initialMedication"
            :parent-bag="parentBag"
            :readonly="readonly"
            :errors="initialMedErrors"
            @update="(field, value) => updateField(field, value)"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import get from 'lodash/get';

import MedicationFluidSelector from '~lib/case-logic-builder/controls/common/MedicationFluidSelector.vue';
import InfusionBagVolumeSelect from '~lib/common/components/medications/InfusionBagVolumeSelect.vue';
import HelpTooltip from '~lib/common/components/HelpTooltip';

import InitialControl from './InitialControl.vue';
import InitialConnection from './InitialConnection.vue';

const ALL_PANELS = ['MEDICATION_FLUID_SELECT', 'CONTROL', 'CONNECTION' ];

export default {
  name: 'initial-medication',
  components: {
    MedicationFluidSelector,
    InfusionBagVolumeSelect,
    InitialControl,
    InitialConnection,
    HelpTooltip,
  },
  props: {
    caseConf: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    initialMedication: {
      type: Object,
      required: true,
    },
    parentBag: {
      type: Object,
      default: () => null,
    },
    medications: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      state: {
        openPanels: ALL_PANELS,
        isUpdating: false,
      },
    };
  },
  computed: {
    selectedDosageForm() {
      const { medOrFluidId: medicationId } = this.initialMedication;
      if (!this.caseConf) {
        return null;
      }
      return this.caseConf.dosageForms.find(
        caseDosageForm => caseDosageForm.medicationId === medicationId,
      );
    },
    selectedFluid() {
      const { medOrFluidId: fluidKey } = this.initialMedication;
      if (!this.caseConf) {
        return null;
      }
      return this.caseConf.fluids.find(fluid => fluid.key === fluidKey);
    },
    selectedDosageFormInfusionRoute() {
      if (!this.selectedDosageForm) {
        return null;
      }
      return this.selectedDosageForm.enabledRoutes.find(
        route => route.key === 'IV_IO_INFUSION',
      );
    },
    availableBagVolumes() {
      const route = this.selectedDosageFormInfusionRoute;
      if (route) {
        return route.options.infusionBagVolumes || [];
      } else {
        const fluid = this.selectedFluid;
        if (fluid) {
          return fluid.infusionBagVolumes;
        }
      }
      return [];
    },
    initialMedErrors() {
      return this.$store
        ? get(
          this.$store.getters['validation/errorsByFieldAndId'],
          `initialMedications.${this.initialMedication.id}`
        ) || [] : [];
    },
    medEnabledError() {
      return this.initialMedErrors.find(error => error.metadata.type === 'medEnabled');
    },
    routeEnabledError() {
      return this.initialMedErrors.find(error => error.metadata.type === 'routeEnabled');
    },
    fluidEnabledError() {
      return this.initialMedErrors.find(error => error.metadata.type === 'fluidEnabled');
    },
    volumeEnabledError() {
      return this.initialMedErrors.find(error => error.metadata.type === 'volumeEnabled');
    },
  },
  watch: {
    initialMedication() {
      // Prevent updates when switching between tabs
      this.state.isUpdating = true;
      this.$nextTick(() => {
        this.state.isUpdating = false;
      });
    },
  },
  methods: {
    isActivePanel(panel) {
      return this.state.openPanels.includes(panel);
    },
    togglePanel(panel) {
      if (this.isActivePanel(panel)) {
        this.state.openPanels = this.state.openPanels.filter(p => p !== panel);
      } else {
        this.state.openPanels.push(panel);
      }
    },
    selectMedicationOrFluid(treatmentType, id) {
      this.$emit('update', {
        ...this.initialMedication,
        treatmentType,
        medOrFluidId: id,
      });
    },
    updateField(field, value) {
      if (this.state.isUpdating) {
        return;
      }
      this.state.isUpdating = true;
      const update = {
        [field]: value,
      };

      if (field === 'isDropSetEnabled' && value) {
        // Disable iv pump and clear its values
        update.isIvPumpEnabled = false;
        update.ivPumpPrimary = null;
        update.ivPumpSecondary = null;
      }

      if (field === 'isIvPumpEnabled' && value) {
        // Disable drop and clear its values
        update.isDropSetEnabled = false;
        update.rate = null;
        update.dripSet = null;
      }

      this.$emit('update', {
        ...this.initialMedication,
        ...update,
      });
      this.$nextTick(() => {
        this.state.isUpdating = false;
      });
    },
  },
};
</script>

<style scoped>
.initial-medication {
  padding-top: 8px;
  padding-bottom: 8px;
}
.accordion {
  width: 100%;
}

.accordion-item {
  display: flex;
  flex-direction: column;
}

.accordion-trigger {
  background-color: #fcfcfd !important;
  margin: 0;
  text-align: left;
  margin-top: 5px;
  height: 40px;
  z-index: 6;
}
.accordion-trigger.open .trigger-icon {
  transform: rotateZ(90deg);
}
.accordion-trigger label {
  vertical-align: middle;
}

.accordion-content {
  background-color: #fcfcfd;
  border: solid 1px #d2d2d2;
  border-radius: 0px 0px 4px 4px;
  margin-bottom: 15px;
  padding-top: 10px;
  padding: 1rem;
  display: flex;
  flex-direction: row;
}

.control-content {
  display: flex;
  flex-direction: column;
}

.med-fluid-container {
  margin-right: 16px;
}
</style>

<style>
.initial-medication .accordion-trigger .md-ripple {
  justify-content: flex-start;
}
</style>